import React from 'react';
import type { ComponentConfig } from '@puckeditor/core';
import styled from 'styled-components';

import { ColorRoleField } from '../fields/widgets/color-role-field';
import { createSpacingFields } from '../fields/spacing-fields';
import {
  BODY_LINE_HEIGHT,
  BODY_TEXT_SIZES,
  FAQ_ANSWER_INSET,
  FAQ_DIVIDER_WIDTH,
  FAQ_HEADING_GAP,
  FAQ_QUESTION_FONT_WEIGHT,
  FAQ_QUESTION_GAP,
  FAQ_QUESTION_INSET,
  FONT_ROLES,
  HEADING_PRESETS,
  lookup,
  MAX_WIDTHS,
  NO_SPACING,
  resolveTextColor,
  spacingToCss,
  SURFACE_COLOR_ROLES,
  TEXT_COLOR_ROLES,
} from '../fields/tokens';
import type { BoxSpacing, FontRole, HeadingPreset, MaxWidthOption, SurfaceColorRole } from '../fields/tokens';

/**
 * Disclosure glyphs, swapped by CSS content so the open/closed state needs no JS.
 * `details[open]` drives the change.
 *
 * The caret rotates a single glyph rather than swapping U+2304 (⌄) for U+2303 (⌃). Those are
 * independently designed codepoints with no guarantee of matching optical size, and U+2304 has
 * patchy font coverage, so on some platforms it falls back to a different font entirely — which
 * rendered the two states at visibly different sizes. Rotating one glyph makes the two states
 * identical by construction, on every font and platform.
 *
 * Plus/minus still swap: U+002B and U+2212 are ubiquitous and metrically consistent.
 */
const DISCLOSURE_ICONS = {
  caret: { collapsed: "'⌃'", expanded: "'⌃'", collapsedTransform: 'rotate(180deg)', expandedTransform: 'none' },
  plus: { collapsed: "'+'", expanded: "'−'", collapsedTransform: 'none', expandedTransform: 'none' },
} as const;

type DisclosureIcon = keyof typeof DISCLOSURE_ICONS;

/**
 * Semantic heading levels available for the FAQ section heading.
 * H1 is included for standalone FAQ pages where the block's heading legitimately
 * serves as the page's top-level heading.
 */
const FAQ_HEADING_LEVELS = ['h1', 'h2', 'h3', 'h4', 'h5', 'h6'] as const;

type FaqHeadingLevel = typeof FAQ_HEADING_LEVELS[number];

/**
 * Size can track the semantic level instead of pinning a step. Mirrors the same
 * constant in heading.tsx — both blocks use the stored value `'match'` so a future
 * shared preset field can be introduced without a data migration.
 *
 * Not imported from heading.tsx because that constant is not exported there; a tiny
 * local mirror is preferable to restructuring a sibling file just to share one string.
 */
const FAQ_HEADING_SIZE_MATCH = 'match' as const;

type FaqHeadingSize = HeadingPreset | typeof FAQ_HEADING_SIZE_MATCH;

/**
 * Named icon sizes for the disclosure glyph. Drives the font-size of the ::after pseudo-element
 * as a relative (em) value so the glyph scales with the inherited question font-size token rather
 * than being severed from it.
 *
 * medium = 1em reproduces the pre-change behaviour exactly: the ::after previously had no
 * font-size override and therefore inherited the summary's `font-size: var(--cms-faq-question-font-size, …)`
 * directly — which is exactly what 1em does. A merchant theme overriding that token still scales
 * the glyph in lockstep.
 *
 * small = 0.85em (~15–16px at 18px base), large = 1.35em (~24px at 18px base).
 */
const FAQ_ICON_SIZES = {
  small: '0.85em',
  medium: '1em',
  large: '1.35em',
} as const;

type FaqIconSize = keyof typeof FAQ_ICON_SIZES;

type TextAlign = 'center' | 'left';

type FaqRow = {
  question: string;
  answer: string;
  openByDefault: 'no' | 'yes';
};

export type FaqProps = {
  heading: string;
  /** Semantic heading level for the section heading (H1–H6). Default h3. */
  level: FaqHeadingLevel;
  /** Visual size of the section heading, or `match` to follow `level`. Default `match`. */
  headingSize: FaqHeadingSize;
  rows: FaqRow[];
  icon: DisclosureIcon;
  /** Size of the disclosure glyph. */
  iconSize: FaqIconSize;
  dividers: 'hide' | 'show';
  /** Text alignment for heading and question/answer text. */
  align: TextAlign;
  font: FontRole;
  /** Section heading colour. Falls back to `color` on pages saved before it was separable. */
  headingColor?: string;
  /**
   * Question colour. Keeps the key `color` rather than renaming to `questionColor` so pages saved
   * before heading/answer were separable need no migration.
   */
  color: string;
  answerColor: string;
  background: SurfaceColorRole;
  maxWidth: MaxWidthOption;
  padding: BoxSpacing;
};

/**
 * Built on native `<details>`/`<summary>` rather than wrapping an existing accordion.
 * The shared MUI accordion is checkout-specific (its theme override pins `maxWidth: 390`
 * and it uses `AccordionActions` for content), and `CollapsibleSection` hardcodes an
 * uppercase 13px form-label heading — neither can carry the token vocabulary for the
 * question text. Native disclosure also gives keyboard and screen-reader support, and
 * open-by-default, without managed state.
 */
const Wrapper = styled.section`
  background: var(--cms-faq-background, ${SURFACE_COLOR_ROLES.none});
  font-family: var(--cms-faq-font-family, ${FONT_ROLES.body});
  color: var(--cms-faq-color, ${TEXT_COLOR_ROLES.text});
  max-width: var(--cms-faq-max-width, ${MAX_WIDTHS.none});
  padding: var(--cms-faq-padding, 0px);
  text-align: var(--cms-faq-text-align, left);
`;

const SectionHeading = styled.h3`
  margin: 0 0 ${FAQ_HEADING_GAP};
  font-size: var(--cms-faq-heading-font-size, ${HEADING_PRESETS.h3.fontSize});
  font-weight: var(--cms-faq-heading-font-weight, ${HEADING_PRESETS.h3.fontWeight});
  /* Falls back to the inherited question colour, which is what pre-split pages expect. */
  color: var(--cms-faq-heading-color, inherit);
`;

const Row = styled.details`
  border-top: var(--cms-faq-divider, none);

  &:last-of-type {
    border-bottom: var(--cms-faq-divider, none);
  }

  > summary {
    display: flex;
    align-items: center;
    justify-content: var(--cms-faq-summary-justify, space-between);
    gap: ${FAQ_QUESTION_GAP};
    padding: ${FAQ_QUESTION_INSET};
    cursor: pointer;
    font-size: var(--cms-faq-question-font-size, ${BODY_TEXT_SIZES.xl});
    font-weight: ${FAQ_QUESTION_FONT_WEIGHT};
    list-style: none;
  }

  /* Suppress the native marker so the themeable glyph below is the only one. */
  > summary::-webkit-details-marker {
    display: none;
  }

  > summary::after {
    content: var(--cms-faq-icon-collapsed, "'+'");
    flex: none;
    /* inline-block so the caret's rotation applies; transforms are ignored on inline boxes. */
    display: inline-block;
    transform: var(--cms-faq-icon-collapsed-transform, none);
    color: ${TEXT_COLOR_ROLES.muted};
    font-size: var(--cms-faq-icon-size, ${FAQ_ICON_SIZES.medium});
  }

  &[open] > summary::after {
    content: var(--cms-faq-icon-expanded, "'−'");
    transform: var(--cms-faq-icon-expanded-transform, none);
  }
`;

const Answer = styled.div`
  padding: ${FAQ_ANSWER_INSET};
  white-space: pre-wrap;
  font-size: var(--cms-faq-answer-font-size, ${BODY_TEXT_SIZES.lg});
  line-height: var(--cms-faq-line-height, ${BODY_LINE_HEIGHT});
  color: var(--cms-faq-answer-color, ${TEXT_COLOR_ROLES.secondary});
`;

export const faqConfig: ComponentConfig<FaqProps> = {
  label: 'FAQ',
  ai: {
    instructions:
      'A list of frequently asked questions as expandable rows. Write a real question in each `question` and a complete answer in each `answer`. Keep answers to a short paragraph.',
  },
  fields: {
    heading: { type: 'text', label: 'Heading' },
    level: {
      type: 'select',
      label: 'Heading level',
      options: FAQ_HEADING_LEVELS.map((l) => ({ label: l.toUpperCase(), value: l })),
      ai: { schema: { type: 'string', enum: [...FAQ_HEADING_LEVELS] } },
    },
    headingSize: {
      type: 'select',
      label: 'Heading size',
      options: [
        { label: 'Match level', value: FAQ_HEADING_SIZE_MATCH },
        ...FAQ_HEADING_LEVELS.map((l) => ({ label: `Heading ${l.slice(1)}`, value: l })),
      ],
      ai: {
        schema: { type: 'string', enum: [FAQ_HEADING_SIZE_MATCH, ...FAQ_HEADING_LEVELS] },
        instructions:
          "Visual size of the section heading. Leave as 'match' so it follows the heading level; override only when design needs a size that differs from the document outline.",
      },
    },
    rows: {
      type: 'array',
      label: 'Questions',
      getItemSummary: (row) => row.question || 'New question',
      defaultItemProps: { question: 'New question', answer: '', openByDefault: 'no' },
      arrayFields: {
        question: { type: 'text', label: 'Question' },
        answer: { type: 'textarea', label: 'Answer' },
        openByDefault: {
          type: 'radio',
          label: 'Open by default',
          options: [
            { label: 'No', value: 'no' },
            { label: 'Yes', value: 'yes' },
          ],
        },
      },
      ai: {
        instructions: 'Between three and eight rows reads best. Only open the first row by default, if any.',
      },
    },
    icon: {
      type: 'radio',
      label: 'Icon',
      options: [
        { label: 'Caret', value: 'caret' },
        { label: 'Plus', value: 'plus' },
      ],
      ai: { schema: { type: 'string', enum: ['caret', 'plus'] } },
    },
    iconSize: {
      type: 'radio',
      label: 'Icon size',
      options: [
        { label: 'Small', value: 'small' },
        { label: 'Medium', value: 'medium' },
        { label: 'Large', value: 'large' },
      ],
      ai: { schema: { type: 'string', enum: ['small', 'medium', 'large'] } },
    },
    dividers: {
      type: 'radio',
      label: 'Dividers',
      options: [
        { label: 'Show', value: 'show' },
        { label: 'Hide', value: 'hide' },
      ],
      ai: { schema: { type: 'string', enum: ['show', 'hide'] } },
    },
    align: {
      type: 'radio',
      label: 'Alignment',
      options: [
        { label: 'Left', value: 'left' },
        { label: 'Center', value: 'center' },
      ],
      ai: { schema: { type: 'string', enum: ['left', 'center'] } },
    },
    font: {
      type: 'radio',
      label: 'Font',
      options: [
        { label: 'Heading', value: 'heading' },
        { label: 'Body', value: 'body' },
      ],
      ai: { schema: { type: 'string', enum: ['heading', 'body'] } },
    },
    headingColor: {
      type: 'custom',
      label: 'Heading color',
      render: ColorRoleField,
      ai: {
        schema: {
          type: 'string',
          description:
            "Colour of the section heading. A theme colour role ('text', 'secondary', 'muted', 'inverted', 'link') or a hex value.",
        },
      },
    },
    color: {
      type: 'custom',
      label: 'Question color',
      render: ColorRoleField,
      ai: {
        schema: {
          type: 'string',
          description:
            "Colour of the question rows. A theme colour role ('text', 'secondary', 'muted', 'inverted', 'link') or a hex value.",
        },
      },
    },
    answerColor: {
      type: 'custom',
      label: 'Answer color',
      render: ColorRoleField,
      ai: {
        schema: {
          type: 'string',
          description:
            "Colour of the answer text. Defaults to the 'secondary' role so answers read as a lower tier than questions.",
        },
      },
    },
    background: {
      type: 'radio',
      label: 'Background',
      options: [
        { label: 'Transparent', value: 'none' },
        { label: 'Surface', value: 'surface' },
        { label: 'Muted', value: 'muted' },
      ],
      ai: { schema: { type: 'string', enum: ['none', 'surface', 'muted'] } },
    },
    maxWidth: {
      type: 'radio',
      label: 'Max width',
      options: [
        { label: 'Narrow', value: 'narrow' },
        { label: 'Normal', value: 'normal' },
        { label: 'Full', value: 'none' },
      ],
      ai: { schema: { type: 'string', enum: ['narrow', 'normal', 'none'] } },
    },
    padding: {
      type: 'object',
      label: 'Padding',
      objectFields: createSpacingFields(),
    },
  },
  defaultProps: {
    heading: 'Frequently asked questions',
    level: 'h3',
    headingSize: FAQ_HEADING_SIZE_MATCH,
    rows: [
      { question: 'What is your return policy?', answer: '', openByDefault: 'no' },
      { question: 'How long does delivery take?', answer: '', openByDefault: 'no' },
    ],
    icon: 'plus',
    iconSize: 'medium',
    dividers: 'show',
    align: 'left',
    font: 'body',
    headingColor: 'text',
    color: 'text',
    answerColor: 'secondary',
    background: 'none',
    maxWidth: 'normal',
    padding: NO_SPACING,
  },
  render: ({
    heading = '',
    level = 'h3',
    headingSize = FAQ_HEADING_SIZE_MATCH,
    rows,
    icon = 'plus',
    iconSize = 'medium',
    dividers = 'show',
    align = 'left',
    font = 'body',
    headingColor,
    color = 'text',
    answerColor = 'secondary',
    background = 'none',
    maxWidth = 'normal',
    padding,
  }) => {
    const glyphs = lookup(DISCLOSURE_ICONS, icon, DISCLOSURE_ICONS.plus);
    const items = Array.isArray(rows) ? rows : [];
    const sectionLevel = FAQ_HEADING_LEVELS.includes(level) ? level : 'h3';
    const iconSizePx = lookup(FAQ_ICON_SIZES, iconSize, FAQ_ICON_SIZES.medium);
    const sizeKey = headingSize === FAQ_HEADING_SIZE_MATCH ? sectionLevel : headingSize;
    const headingSizing = lookup(HEADING_PRESETS, sizeKey, HEADING_PRESETS.h3);

    return (
      <Wrapper
        style={
          {
            '--cms-faq-background': lookup(SURFACE_COLOR_ROLES, background, SURFACE_COLOR_ROLES.none),
            '--cms-faq-font-family': lookup(FONT_ROLES, font, FONT_ROLES.body),
            '--cms-faq-color': resolveTextColor(color),
            // Pages saved before the heading colour was separable carry no `headingColor`; falling
            // back to the question colour keeps them looking exactly as they did.
            '--cms-faq-heading-color': resolveTextColor(headingColor ?? color),
            '--cms-faq-heading-font-size': headingSizing.fontSize,
            '--cms-faq-heading-font-weight': headingSizing.fontWeight,
            '--cms-faq-answer-color': resolveTextColor(answerColor),
            '--cms-faq-max-width': lookup(MAX_WIDTHS, maxWidth, MAX_WIDTHS.normal),
            '--cms-faq-padding': spacingToCss(padding),
            '--cms-faq-text-align': align,
            // When centred the icon stays in the flex row next to the text (justify-content: center
            // centres the whole row together). This is preferable to keeping space-between, which
            // would pin the icon to the right edge while the text centres — a mismatched layout.
            '--cms-faq-summary-justify': align === 'center' ? 'center' : 'space-between',
            '--cms-faq-icon-collapsed': glyphs.collapsed,
            '--cms-faq-icon-expanded': glyphs.expanded,
            '--cms-faq-icon-collapsed-transform': glyphs.collapsedTransform,
            '--cms-faq-icon-expanded-transform': glyphs.expandedTransform,
            '--cms-faq-icon-size': iconSizePx,
            '--cms-faq-divider':
              dividers === 'show' ? `${FAQ_DIVIDER_WIDTH} solid ${TEXT_COLOR_ROLES.secondary}` : 'none',
          } as React.CSSProperties
        }
      >
        {heading && <SectionHeading as={sectionLevel}>{heading}</SectionHeading>}
        {items.map((row, index) => (
          // `open` on a native <details> is uncontrolled: the element mutates its own state when the
          // author clicks it, and React only reasserts the attribute when the computed value changes.
          // Keying purely by index would therefore let a manually expanded row keep its DOM node
          // through a drag-reorder, so the expansion would follow the list position rather than the
          // item the author opened. Including the question makes the key change on reorder, which
          // remounts the rows and re-derives `open` from props. Rows carry no stable id, so index
          // stays in the key to keep it unique when two questions read the same.
          // eslint-disable-next-line react/no-array-index-key
          <Row key={`${index}:${row.question}`} open={row.openByDefault === 'yes'}>
            {/* Question text is a plain text node, not a heading: <summary> has an implicit ARIA
                role of button whose presentational-children requirement strips nested heading
                semantics (JAWS/TalkBack do not expose them). Rebuild as APG accordion: ECOMM-12770. */}
            <summary>{row.question}</summary>
            {row.answer && <Answer>{row.answer}</Answer>}
          </Row>
        ))}
      </Wrapper>
    );
  },
};
